import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useRefresh, triggerRefresh } from "@/hooks/useRefresh";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Building2 } from "lucide-react";

const EMPTY = { name: "", address: "", contact: "" };

export default function Branches() {
  const { branches, reload, role } = useBranch();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useRefresh(() => reload(), []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/branches", form);
      else await api.put(`/branches/${editing}`, form);
      toast.success("Branch saved");
      setEditing(null);
      await reload();
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="branches-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Branches</h1>
        {role === "owner" && (
          <Button onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="add-branch-btn">
            <Plus className="h-4 w-4 mr-1" /> Add branch
          </Button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((b) => (
          <div key={b.branch_id} className="border rounded-xl bg-card p-5 hover:-translate-y-0.5 hover:shadow-sm transition-transform" data-testid={`branch-card-${b.branch_id}`}>
            <div className="flex items-start justify-between">
              <Building2 className="h-6 w-6 text-primary" />
              {role === "owner" && (
                <Button size="sm" variant="ghost" onClick={() => { setForm(b); setEditing(b.branch_id); }} data-testid={`edit-branch-${b.branch_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <h3 className="font-bold mt-3">{b.name}</h3>
            <p className="text-sm text-muted-foreground mt-1">{b.address || "No address"}</p>
            <p className="text-xs text-muted-foreground mt-1">{b.contact}</p>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="branch-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "Add branch" : "Edit branch"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Name</Label>
              <Input data-testid="branch-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Address</Label>
              <Input data-testid="branch-address-input" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-2"><Label>Contact</Label>
              <Input data-testid="branch-contact-input" value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <Button type="submit" className="w-full" data-testid="branch-save-btn">Save branch</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
